<template lang="html">
  <v-content>
    <iframe id="my_iframe" style="display: none"></iframe>

    <EditDocumentTagDialog
      :opened.sync="editDocumentTagDialogOpened"
      :selected-item="selectedItem"
      @save-document-tags="saveDocumentTags"
    />

    <DownloadFolderDialog
      v-if="currentCompanyId"
      :opened.sync="downloadFolderDialogOpened"
      :folder="targetFolder"
      :current-company-id="currentCompanyId"
    />

    <DownloadMultipleDialog
      v-if="currentCompanyId"
      :opened.sync="downloadMultipleDialogOpened"
      :documents="selectedDocuments"
      :current-company-id="currentCompanyId"
    />

    <SelectQuestionnaireDialog
      v-if="currentCompanyId"
      :opened.sync="attachToQuestionnaireDialogOpened"
      :selected-items="selectedDocuments"
      :questionnaires="clientQuestionnaires"
      :current-company-id="currentCompanyId"
      @refresh-documents="getDocuments(currentPath)"
    />

    <PDFEditor
      :opened.sync="pdfEditorOpened"
      :pdf-src="documentPreviewData"
      :path="currentPath"
      :file-name="selectedItem.name"
      :current-company-id="currentCompanyId"
      @on-saved="onPdfSaved"
    />

    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <!-- ActivityLog Dialog -->
    <v-dialog
      v-model="activityLogDialogOpened"
      width="800"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="activityLogDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title">
          Document History for {{ currentCompany ? currentCompany.name : null }}
          <v-spacer />
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="activityLogDialogOpened = false"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <activity-log
            v-if="currentCompanyId"
            :dialog-opened="activityLogDialogOpened"
            :current-company-id="currentCompanyId"
            :type-id="2"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Move Files Dialog -->
    <v-dialog
      width="600"
      v-model="moveItemDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveItemDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title"> Specify new location </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">{{ deleteOldPath ? 'Move' : 'Copy' }} to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              label="Different entity"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              placeholder="Select new entity"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath) ? 'No folders' : 'Choose folder'
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath) || loading"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveItem()"
            :disabled="
              pathsInOrder.join('/') === currentPath && !newEntityLocation
            "
          >
            {{ deleteOldPath ? 'Move' : 'Copy' }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveItemDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Move Multiple Files Dialog -->
    <v-dialog
      width="600"
      v-model="moveMultipleDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveMultipleDocumentsDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title"> Specify new location </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">{{ deleteOldPath ? 'Move' : 'Copy' }} to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              label="Different entity"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              placeholder="Select new entity"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath) ? 'No folders' : 'Choose folder'
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath)"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveMultipleDocuments()"
            :disabled="pathsInOrder.join('/') === currentPath"
          >
            {{ deleteOldPath ? 'Move' : 'Copy' }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveMultipleDocumentsDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteConfirmationDialogOpened"
      @confirm="deleteDocument(documentToDelete)"
    >
      Are you sure you want to delete <b>{{ documentToDelete.name }}</b
      >?
    </ConfirmationDialog>

    <!-- Delete Multiple Files Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteMultipleConfirmationDialogOpened"
      @confirm="deleteMultipleDocuments()"
    >
      Are you sure you want to delete {{ selectedDocuments.length }} items?
    </ConfirmationDialog>

    <!-- Download Multiple Files Confirmation Dialog -->
    <ConfirmationDialog
      v-model="downloadMultipleConfirmationDialogOpened"
      @confirm="downloadMultipleDocuments()"
    >
      Are you sure you want to download
      {{ selectedDocuments.filter(doc => doc.type === 'file').length }} files?
      (You cannot download folders) You might have to enable pop up's on the top
      if multiple files are not downloading.
    </ConfirmationDialog>

    <!-- Save Annotation Dialog -->
    <v-dialog
      v-model="saveAnnotationsDialogOpened"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="500"
      @keydown.esc="saveAnnotationsDialogOpened = false"
      @keydown.enter="saveAnnotations()"
    >
      <v-card>
        <v-card-title class="subheading">
          Do you want to save all changes to the annotations?
          <v-spacer />
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="saveAnnotationsDialogOpened = false"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn class="text-capitalize primary" @click="saveAnnotations()">
            Save
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="closePreviewDialog()"
          >
            Do not save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Dialog -->
    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown || fullscreenDialog"
      v-model="previewDialogOpened"
      persistent
      @keydown.esc="checkAnnotationSaveConfirmation()"
    >
      <v-card v-if="selectedItem && previewType">
        <v-card-title class="title pb-0">
          <v-layout v-if="renameFile" align-center no-wrap>
            <span class="caption">New Name:</span>
            <v-text-field
              v-model="newName"
              hide-details
              solo
              class="default-input mx-2"
            />
            <span class="subheading">.{{ documentExtension }}</span>
            <v-tooltip bottom class="mx-2">
              <i
                class="material-icons-outlined success--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameItem()"
                slot="activator"
              >
                check
              </i>
              <span>Save</span>
            </v-tooltip>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined error--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameFile = false"
                slot="activator"
              >
                cancel
              </i>
              <span>{{ $t('cancel') }}</span>
            </v-tooltip>
          </v-layout>
          <v-layout v-else>
            <span class="text-truncate" style="max-width: 500px">{{
              selectedItem.name
            }}</span>
          </v-layout>
          <v-spacer />

          <v-btn
            v-if="selectedItem.name.toLowerCase().endsWith('pdf')"
            class="ma-0 text-none"
            color="primary"
            @click="pdfEditorOpened = true"
          >
            Edit
          </v-btn>

          <v-tooltip
            bottom
            class="mx-2"
            v-if="
              annotationEnabled &&
                !showAnnotations &&
                isEmpty(this.annotations) &&
                endsWithAny(['pdf'], selectedItem.name.toLowerCase()) &&
                selectedItem.extracted_document_id
            "
          >
            <v-btn
              class="primary--text ma-0"
              small
              flat
              fab
              @click="showAnnotations = true"
              slot="activator"
            >
              <v-icon size="25">edit</v-icon>
            </v-btn>
            <span>Start Annotation</span>
          </v-tooltip>
          <v-tooltip
            bottom
            class="mx-2"
            v-else-if="
              annotationEnabled && showAnnotations && isEmpty(this.annotations)
            "
          >
            <v-btn
              class="error--text ma-0"
              flat
              small
              fab
              @click="showAnnotations = false"
              slot="activator"
            >
              <v-icon size="25">cancel</v-icon>
            </v-btn>
            <span>Cancel Annotation</span>
          </v-tooltip>
          <v-switch
            class="default-input pa-0 ma-0"
            solo
            :label="'Annotations (' + this.annotations.length + ')'"
            v-model="showAnnotations"
            hide-details
            v-if="isAdvisor && annotationEnabled && !isEmpty(this.annotations)"
          />
          <v-menu
            transition="slide-y-transition"
            bottom
            left
            nudge-bottom="40"
            class="my-1"
          >
            <v-tooltip slot="activator" bottom>
              <v-btn
                slot="activator"
                fab
                small
                flat
                class="white text-capitalize ma-0 ml-1"
                @click=""
              >
                <v-icon size="25"> more_vert </v-icon>
              </v-btn>
              <span>Actions</span>
            </v-tooltip>
            <v-list>
              <v-list-tile @click="startRenaming()">
                <v-list-tile-title> Rename </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                @click="openEditTags(selectedItem)"
                v-if="currentFirm.document_tags_enabled"
              >
                <v-list-tile-title> Edit Tags </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="openMoveDialog(selectedItem)">
                <v-list-tile-title> Move </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="openCopyDialog(selectedItem)">
                <v-list-tile-title> Copy </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                @click="openAttachToQuestionnaireDialog(selectedItem)"
                v-if="
                  selectedItem.type !== 'folder' &&
                    isAdvisor &&
                    !selectedItem.questionnaire_id
                "
              >
                <v-list-tile-title> Attach to Questionnaire </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="downloadDocument(selectedItem, false)">
                <v-list-tile-title> Download </v-list-tile-title>
              </v-list-tile>
              <v-list-tile
                v-if="
                  annotationEnabled && !isEmpty(this.annotations) && isAdvisor
                "
                @click="downloadDocument(selectedItem, true)"
              >
                <v-list-tile-title>
                  Download with Annotations
                </v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="openDeleteConfirmationDialog(selectedItem)">
                <v-list-tile-title> Delete </v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <v-tooltip bottom class="mx-1 hidden-sm-and-down">
            <v-btn
              slot="activator"
              fab
              small
              flat
              :class="fullscreenDialog ? 'primary--text' : ''"
              class="white text-capitalize ma-0 ml-1"
              @click="fullscreenDialog = !fullscreenDialog"
            >
              <v-icon size="25"> fullscreen </v-icon>
            </v-btn>
            <span>Fullscreen</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="checkAnnotationSaveConfirmation()"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :selected-item="selectedItem"
            :annotations="annotations"
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
            :show-annotations="showAnnotations"
            :current-company="currentCompany"
            @save-annotations="saveAnnotations()"
            :annotations-changed.sync="annotationsChanged"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Import Documents Dialog -->
    <v-dialog
      width="600"
      v-model="importDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="importDocumentsDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="title pb-0">
          {{ $t('documents.uploadDocuments') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            {{ $t('alerts.dragAndDropFiles') }}
          </v-alert>
          <v-layout column>
            <v-flex class="text-xs-left mb-2 font-weight-bold">
              <v-layout align-center>
                <div class="py-3">{{ $t('documents.selectedDocuments') }}:</div>
                <v-spacer />
                <v-progress-circular
                  v-if="documentImporting"
                  indeterminate
                  size="25"
                  class="primary--text mr-2"
                />
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  :disabled="documentImporting"
                  @click="pickDocuments"
                >
                  <v-layout align-center>
                    <v-icon class="mr-2" size="20">add</v-icon>
                    {{ $t('documents.selectFiles') }}
                  </v-layout>
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
              No documents selected.
            </v-flex>
            <v-flex class="mb-4" v-else>
              <v-layout
                align-center
                :key="i"
                v-for="(doc, i) in documentsToImport"
              >
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ doc.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentsToImport.splice(i, 1)"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="importDocuments()"
            :disabled="documentImporting || isEmpty(documentsToImport)"
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              importDocumentsDialogOpened = false;
              documentsToImport = [];
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Folder Dialog -->
    <v-dialog
      width="600"
      v-model="createFolderDialogOpened"
      persistent
      @keydown.esc="createFolderDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title pb-0"> Create Folder </v-card-title>
        <v-card-text>
          <v-alert
            v-if="isAdvisor"
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            Do not include slashes (/) or periods (.) and add
            <b>STAFF ONLY</b> in the folder name if you want the folder to be
            hidden from the client's view.
          </v-alert>
          <v-alert
            v-else
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            Do not include slashes (/) or periods (.).
          </v-alert>
          <v-layout class="text-xs-left" column>
            <v-flex>
              <v-flex class="mb-2">Folder Name</v-flex>
              <v-text-field
                v-model="newFolderName"
                solo
                class="default-input"
                placeholder="e.g. Tax Returns"
                hide-details
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="createFolder"
            :disabled="
              !newFolderName.length ||
                newFolderName.includes('/') ||
                newFolderName.includes('.')
            "
          >
            Create
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="createFolderDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Documents: New -->
    <new-documents
      :loading="loading"
      :paths="paths"
      :documents="documents"
      :customDocumentFilter="customDocumentFilter"
      :selectedDocuments.sync="selectedDocuments"
      :documentExtension="documentExtension"
      :searchingTerm="searchingTerm"
      :searchTerm.sync="searchTerm"
      :newName.sync="newName"
      :clientQuestionnaires="clientQuestionnaires"
      @openImportDocumentsDialog="openImportDocumentsDialog"
      @openCreateFolderDialog="createFolderDialogOpened = true"
      @openActivityLogDialog="activityLogDialogOpened = true"
      @goToPath="goToPath"
      @goToFolder="goToFolder"
      @searchDocuments="searchDocuments"
      @previewDocument="previewDocument"
      @startRenaming="startRenaming"
      @renameItem="renameItem"
      @cancelRenaming="cancelRenaming"
      @openMoveDialog="openMoveDialog"
      @openCopyDialog="openCopyDialog"
      @openMoveMultipleDocumentsDialog="openMoveMultipleDocumentsDialog"
      @openDeleteConfirmationDialog="openDeleteConfirmationDialog"
      @openDeleteMultipleConfirmationDialog="
        deleteMultipleConfirmationDialogOpened = true
      "
      @openDownloadMultipleDialog="downloadMultipleDialogOpened = true"
      @openAttachToQuestionnaireDialog="openAttachToQuestionnaireDialog"
      @detachFromQuestionnaire="detachFromQuestionnaire"
      @openEditTags="openEditTags"
      @downloadFolder="downloadFolder"
      @downloadDocument="downloadDocument"
      @startEditingDescription="startEditingDescription"
      @saveNewDescription="saveNewDescription"
      @cancelEditingDescription="cancelEditingDescription"
    />
  </v-content>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import PreviewDocument from '@/components/PreviewDocument';
import PDFEditor from '@/components/dialogs/PDFEditor';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import EditDocumentTagDialog from '@/components/dialogs/EditDocumentTagDialog';
import SelectQuestionnaireDialog from '@/components/dialogs/SelectQuestionnaireDialog';
import DownloadFolderDialog from '@/components/dialogs/DownloadFolderDialog';
import DownloadMultipleDialog from '@/components/dialogs/DownloadMultipleDialog';
import ActivityLog from '@/components/ActivityLog';
import { supportedExtensionsForPreview } from '@/constants/variables';
import heicConvert from 'heic-convert';
import GlobalUiAlert from '@/components/GlobalUiAlert';
import DocumentsSearch from './accountant/DocumentsSearch';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
import has from 'lodash/has';
import NewDocuments from '@/components/NewDocuments';
import SxDocumentIcon from '@/components/sx/SxDocumentIcon';

export default {
  name: 'DocumentsPage',
  mixins: [UtilsMixins, FirmPreferenceMixins],
  components: {
    DocumentsSearch,
    GlobalUiAlert,
    PreviewDocument,
    PDFEditor,
    ActivityLog,
    ConfirmationDialog,
    EditDocumentTagDialog,
    DownloadFolderDialog,
    DownloadMultipleDialog,
    SelectQuestionnaireDialog,
    NewDocuments,
    SxDocumentIcon,
  },
  data() {
    return {
      active: 0,
      searchTerm: '',
      searchingTerm: '',
      selectedTagsToFilter: [],
      editDocumentTagDialogOpened: false,
      pdfEditorOpened: false,
      showAnnotations: false,
      previousTitle: '',
      supportedExtensionsForPreview,
      documentPreviewData: '',
      pdfLoaded: false,
      previewType: '',
      previewDialogOpened: false,
      currentPage: 1,
      renameFile: false,
      pageCount: 1,
      deleteOldPath: false,
      documentNameRules: [
        v => !!v || 'This field is required.',
        v => !v.includes('.') || 'Document Name cannot have a period',
        v => !v.includes('/') || 'Document Name cannot have a slash',
      ],
      presenceRule: [v => !!v || 'This field is required.'],
      createFolderDialogOpened: false,
      // uploadDocumentDialogOpened: false,
      deleteConfirmationDialogOpened: false,
      deleteMultipleConfirmationDialogOpened: false,
      downloadMultipleConfirmationDialogOpened: false,
      downloadMultipleDialogOpened: false,
      importDocumentsDialogOpened: false,
      renameItemDialogOpened: false,
      selectedItem: {
        name: '',
        path: null,
        type: 'file',
      },
      lastPathInOrder: {
        name: '',
        type: 'file',
      },
      newName: '',
      documentToDelete: {
        name: '',
      },
      newEntityLocation: false,
      documentsToImport: [],
      selectedDocuments: [],
      documentName: '',
      previewRotation: 0,
      documentActualName: '',
      documentExtension: '',
      documentData: '',
      newFolderName: '',
      documentSearch: '',
      documentImporting: false,
      loading: false,
      previewLoaded: false,
      documents: [],
      currentPath: '',
      currentFolder: 'All Documents',
      documentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '60%',
        },
        {
          text: 'Last Modified',
          align: 'left',
          sortable: true,
          value: 'updated_at',
          width: '30%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '15%',
        },
      ],
      documentHeadersWithTags: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '40%',
        },
        {
          text: 'Tags',
          align: 'left',
          sortable: false,
          value: 'tags',
          width: '35%',
        },
        {
          text: 'Last Modified',
          align: 'left',
          sortable: true,
          value: 'updated_at',
          width: '15%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '10%',
        },
      ],
      fullscreenDialog: false,
      mobileDocumentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '95%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ],
      files: [],
      dragAndDropFileDialogOpened: true,
      moveItemDialogOpened: false,
      moveMultipleDocumentsDialogOpened: false,
      pathsInOrder: [],
      itemsInLastPath: [],
      newCompanyId: null,
      activityLogDialogOpened: false,
      currentCompanyId: null,
      currentCompany: {
        prior_year_items: [],
        spouse_info: {},
        owner_ids: [],
        user: {
          owned_company_ids: [],
        },
      },
      annotations: [],
      saveAnnotationsDialogOpened: false,
      annotationsChanged: false,
      downloadFolderDialogOpened: false,
      targetFolder: null,
      attachToQuestionnaireDialogOpened: false,
      selectedQuestionnaire: null,
      clientQuestionnaires: [],
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      isFirmDocumentsDisabled: 'isFirmDocumentsDisabled',
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
      defaultCompany: 'defaultCompany',
    }),
    ...mapGetters(['currentFirm']),
    uploadDisabled() {
      return this.files.length === 0;
    },
    documentUploadReady() {
      return !isEmpty(this.documentActualName) && !isEmpty(this.documentName);
    },
    displayName() {
      if (this.currentCompany.entity_type === 'Individual') {
        return this.currentCompany.tax_return_name;
      } else {
        return `${this.currentCompany.name} (${this.currentCompany.user.full_name})`;
      }
    },
    otherUserCompanies() {
      if (!this.currentCompany) {
        return [];
      }

      if (this.currentCompany.user.is_advisor) {
        return this.companies.filter(c => {
          return (
            c.user.id === this.currentCompany.user.id &&
            c.id !== this.currentCompanyId
          );
        });
      } else {
        return this.companies.filter(c => {
          return (
            this.currentCompany.user.owned_company_ids.includes(c.id) &&
            c.id !== this.currentCompanyId
          );
        });
      }
    },
    paths() {
      const paths = this.currentPath.split('/');
      if (paths) {
        return paths.map((name, index) => {
          return {
            text: name,
            path: paths.slice(0, index + 1).join('/'),
            disabled: paths.length - 1 === index,
          };
        });
      } else {
        return [];
      }
    },
  },
  methods: {
    moment,
    get,
    isEmpty,
    heicConvert,
    customDocumentFilter(items, search) {
      const searchedItem = search.toLowerCase();

      return items.filter(row => {
        let rowExists = true;
        if (searchedItem.length) {
          rowExists = row.name.toLowerCase().includes(searchedItem);
        }

        if (!isEmpty(this.selectedTagsToFilter)) {
          rowExists = this.selectedTagsToFilter.every(
            val => row.tags && row.tags.includes(val),
          );
        }
        return rowExists;
      });
    },
    async saveDocumentTags(selectedTags) {
      try {
        const response = await API.extractedDocument.updateTags({
          companyId: this.currentCompanyId,
          extractedDocumentId: this.selectedItem.extracted_document_id,
          tags: selectedTags,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Tags updated!',
          });
          this.selectedItem.tags = selectedTags;
        }
        this.editDocumentTagDialogOpened = false;
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'There was a problem saving the tags. Please try again after refreshing the page.',
        });
      }
    },
    openEditTags(selectedItem) {
      this.selectedItem = selectedItem;
      this.editDocumentTagDialogOpened = true;
    },
    startRenaming(item) {
      if (item) {
        this.selectedItem = item;
        item.renameFile = true;
      } else {
        this.renameFile = true;
      }
      this.documentExtension = this.selectedItem.name.split('.').pop();
      this.newName = this.selectedItem.name.replace(
        `.${this.documentExtension}`,
        '',
      );
    },
    cancelRenaming(item) {
      item.renameFile = false;
    },
    openMoveMultipleDocumentsDialog(deleteOldPath) {
      this.deleteOldPath = deleteOldPath;
      this.newCompanyId = this.currentCompanyId;
      this.newEntityLocation = false;

      if (!isEmpty(this.searchingTerm)) {
        this.resetPath();
      } else {
        this.pathsInOrder = this.currentPath.split('/');
        this.itemsInLastPath = this.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.lastPathInOrder = this.currentPath.split('/')[
          this.currentPath.split('/').length - 1
        ];
      }
      this.moveMultipleDocumentsDialogOpened = true;
    },
    openMoveDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = true;
      this.newEntityLocation = false;
      this.newCompanyId = this.currentCompanyId;
      this.pathsInOrder = (item.path ? item.path : this.currentPath).split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.moveItemDialogOpened = true;
    },
    openCopyDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = false;
      this.newEntityLocation = false;
      this.newCompanyId = this.currentCompanyId;
      this.pathsInOrder = (item.path ? item.path : this.currentPath).split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.lastPathInOrder = this.currentPath.split('/')[
        this.currentPath.split('/').length - 1
      ];
      this.moveItemDialogOpened = true;
    },
    openAttachToQuestionnaireDialog(item) {
      this.attachToQuestionnaireDialogOpened = true;
      this.selectedDocuments = Array.isArray(item) ? item : [item];
      this.selectedQuestionnaire = null;
    },
    openAttachMultipleToQuestionnaireDialog() {
      this.attachToQuestionnaireDialogOpened = true;
      this.selectedQuestionnaire = null;
    },
    async detachFromQuestionnaire(item) {
      const response = await API.questionnaire.detachFromQuestionnaire({
        companyId: this.currentCompanyId,
        extractedDocumentIds: item.map(d => d.extracted_document_id),
      });
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response1.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Successfully detached.',
        });
        this.getDocuments(this.currentPath);
      }
    },
    async getClientQuestionnaires() {
      const response = await API.questionnaire.getQuestionnairesForYear({
        companyId: this.currentCompanyId,
        year: 'All',
        full: false,
      });
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
        this.clientQuestionnaires = [];
      } else {
        this.clientQuestionnaires = response.data;
      }
    },
    async goBackPathForMove(path, index) {
      if (index !== this.pathsInOrder.length - 1 && !this.loading) {
        this.loading = true;
        this.lastPathInOrder = null;
        this.pathsInOrder = this.pathsInOrder.slice(0, index + 1);
        const response = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path: this.pathsInOrder.join('/'),
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.loading = false;
      }
    },
    async changePath() {
      if (this.newCompanyId) {
        const response2 = await API.integration.getDocuments({
          companyId: this.newCompanyId,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      } else {
        const response2 = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      }
    },
    async resetPath() {
      this.itemsInLastPath = [];
      this.pathsInOrder = [''];
      this.lastPathInOrder = null;
      if (this.newCompanyId) {
        const response = await API.integration.getDocuments({
          companyId: this.newCompanyId,
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
      }
    },
    async moveItem() {
      try {
        this.moveItemDialogOpened = false;
        this.loading = true;

        const oldPath = (this.selectedItem.path
          ? this.selectedItem.path
          : this.currentPath
        ).concat('/', this.selectedItem.name);

        const response1 = await API.integration.moveItem({
          oldPath,
          newPath: this.pathsInOrder
            .join('/')
            .concat('/', this.selectedItem.name),
          deleteOldPath: this.deleteOldPath,
          companyId: this.currentCompanyId,
          newCompanyId:
            this.newCompanyId === this.currentCompanyId
              ? null
              : this.newCompanyId,
          isFolder: this.selectedItem.type === 'folder',
        });
        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully ${
            this.deleteOldPath ? 'moved' : 'copied'
          } the ${this.selectedItem.type}!`,
        });
        this.previewDialogOpened = false;
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem ${
            this.deleteOldPath ? 'moving' : 'copying'
          } the ${this.selectedItem.type}.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        this.pathsInOrder = [];
      }
    },
    async moveMultipleDocuments() {
      try {
        this.loading = true;
        this.moveMultipleDocumentsDialogOpened = false;

        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (
            doc.name !== '_soraban' &&
            (!this.disableFolderAction || this.isAdvisor || doc.type === 'file')
          ) {
            const oldPath = (doc.path ? doc.path : this.currentPath).concat(
              '/',
              doc.name,
            );
            const response1 = await API.integration.moveItem({
              oldPath,
              newPath: this.pathsInOrder.join('/').concat('/', doc.name),
              deleteOldPath: this.deleteOldPath,
              companyId: this.currentCompanyId,
              newCompanyId:
                this.newCompanyId === this.currentCompanyId
                  ? null
                  : this.newCompanyId,
              isFolder: doc.type === 'folder',
            });
          }
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully ${
            this.deleteOldPath ? 'moved' : 'copied'
          } the documents!`,
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: `There was a problem ${
            this.deleteOldPath ? 'moving' : 'copying'
          } documents.`,
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
      }
    },
    hiddenItem(item) {
      if (this.isAdvisor) {
        return item.type === 'folder' && item.name === '_soraban';
      } else {
        return (
          (item.type === 'folder' && item.name === '_soraban') ||
          item.name.includes('STAFF ONLY')
        );
      }
    },
    async openImportDocumentsDialog() {
      this.documentsToImport = [];
      this.documentImporting = false;
      this.importDocumentsDialogOpened = true;
    },
    async importDocuments() {
      try {
        this.loading = true;
        this.importDocumentsDialogOpened = false;
        const path = this.currentPath;
        const companyId = this.currentCompanyId;
        let success = 0;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(documentToImport);
          const currentExtension = this.getExtensionOfFilename(
            documentToImport.name,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(currentExtension) &&
            trueExtension !== currentExtension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (currentExtension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.currentCompanyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            path,
            companyId,
          });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `There was a problem uploading ${documentToImport.name}.`,
            });
          } else {
            success += 1;
          }
        }

        if (this.documentsToImport.length === success) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You have successfully uploaded ${
              this.documentsToImport.length
            } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
          });
        }

        await this.getDocuments(this.currentPath);
        //
        // if (this.currentPath !== '') {
        //   this.currentFolder = this.currentPath.substring(this.currentPath.lastIndexOf('/') + 1, this.currentPath.length);
        // } else {
        //   this.currentFolder = 'All Documents';
        // }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        this.documentsToImport = [];
      }
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },
    async addFile(e) {
      this.importDocumentsDialogOpened = true;
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f != file;
      });
    },
    async onDocumentsPicked(e) {
      this.documentImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentImporting = false;
      }
    },
    async onPdfSaved() {
      this.previewDialogOpened = false;
      await this.getDocuments(this.currentPath);
    },
    openDeleteConfirmationDialog(documentToDelete) {
      this.documentToDelete = documentToDelete;
      this.deleteConfirmationDialogOpened = true;
    },
    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.target = '_blank';
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },
    async renameItem() {
      try {
        this.loading = true;

        const response1 = await API.integration.renameItem({
          itemName: this.selectedItem.name,
          newName:
            this.selectedItem.type === 'folder'
              ? this.newName
              : this.newName.concat('.', this.documentExtension),
          path: this.selectedItem.path
            ? this.selectedItem.path
            : this.currentPath,
          companyId: this.currentCompanyId,
        });

        if (!isEmpty(this.searchingTerm)) {
          await this.searchDocuments();
        } else {
          await this.getDocuments(this.currentPath);
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully renamed the ${this.selectedItem.type}!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem renaming the document or folder.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        if (!this.previewDialogOpened) {
          this.selectedItem = {
            name: '',
            type: 'file',
          };
        } else {
          this.selectedItem.name = this.newName.concat(
            '.',
            this.documentExtension,
          );
        }
        this.newName = '';
        this.renameFile = false;
        this.documentExtension = '';
      }
    },
    async previewDocument(document) {
      this.previewType = '';
      this.renameFile = false;
      this.previewLoaded = false;
      this.documentPreviewData = '';
      this.previewRotation = 0;
      this.selectedItem = document;
      this.previewType = this.endsWithAny(
        this.supportedExtensionsForPreview,
        document.link,
      );
      if (this.previewType) {
        if (this.previewType === 'pdf') {
          this.currentPage = 1;
          this.pdfLoaded = false;
        }
        this.previewDialogOpened = true;
      }
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompanyId,
          link: document.link,
        });

        if (response.data.document) {
          if (this.previewType === 'heic') {
            this.documentPreviewData = await this.convertToJPEG(
              response.data.document.data.link,
            );
          } else {
            this.documentPreviewData = response.data.document.data.link;
          }

          if (this.annotationEnabled) {
            const response2 = await API.extractedDocument.getAnnotations({
              companyId: this.currentCompanyId,
              extractedDocumentId: document.extracted_document_id,
            });
            if (response2.data.error) {
              this.annotations = [];
              console.log(response2.data.error);
              this.$store.dispatch('application/setErrorNotification', {
                message: 'There was a problem with loading the annotations.',
                errorToSend: response2.data.error,
                userEmail: this.currentUser.email,
              });
            } else {
              this.annotations = response2.data;
            }
          }
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem previewing the document.',
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        }
        if (isEmpty(this.previewType))
          this.downloadDocumentByLink(this.documentPreviewData, document.name);
      } catch (e) {
        this.$store.dispatch('application/setErrorNotification', {
          message: e.message || 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.previewLoaded = true;
      }
    },
    async saveAnnotations() {
      const response = await API.extractedDocument.saveAnnotations({
        companyId: this.currentCompany.id,
        extractedDocumentId: this.selectedItem.extracted_document_id,
        annotations: this.annotations,
      });
      this.previewDialogOpened = false;
      this.documentPreviewData = '';
      this.previewRotation = 0;
      this.saveAnnotationsDialogOpened = false;
      this.annotationsChanged = false;
    },
    checkAnnotationSaveConfirmation() {
      if (this.annotationsChanged) {
        this.saveAnnotationsDialogOpened = true;
      } else {
        this.closePreviewDialog();
      }
    },
    closePreviewDialog() {
      this.previewDialogOpened = false;
      this.saveAnnotationsDialogOpened = false;
      this.documentPreviewData = '';
      this.previewRotation = 0;
      this.selectedItem = {
        name: '',
        type: 'file',
      };
      this.annotations = [];
    },
    async convertToJPEG(url) {
      const output = await fetch(url).then(async data => {
        const buffer = Buffer.from(await data.arrayBuffer());
        return heicConvert({ buffer, format: 'JPEG' });
      });
      const imgBase64 = btoa(
        output.reduce(
          (data, byte) => `${data}${String.fromCharCode(byte)}`,
          '',
        ),
      );
      return `data:image/png;base64,${imgBase64}`;
    },
    async goToFolder(folderName) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = this.currentPath.concat('/', folderName);
          await this.getDocuments(this.currentPath);
          this.currentFolder = folderName;
          this.$router.push({ query: { currentPath: this.currentPath } });
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async goToPath(path) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = path;
          await this.getDocuments(this.currentPath);
          this.$router.push({
            query: { currentPath: this.currentPath || '/' },
          });
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async goBack() {
      if (this.currentFolder === 'All Documents') {
        return;
      }
      try {
        this.loading = true;
        let folderPath = this.currentPath.substring(
          0,
          this.currentPath.lastIndexOf('/'),
        );
        this.currentPath = folderPath;
        await this.getDocuments(this.currentPath);
        if (folderPath !== '') {
          this.currentFolder = this.currentPath.substring(
            this.currentPath.lastIndexOf('/') + 1,
            this.currentPath.length,
          );
        } else {
          this.currentFolder = 'All Documents';
        }
      } finally {
        this.loading = false;
      }
    },
    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    async downloadDocument(doc, includeAnnotations) {
      try {
        if (includeAnnotations) {
          await this.saveAnnotations();
        }
        const response1 = await API.integration.downloadDocument({
          companyId: this.currentCompanyId,
          link: doc.link,
          includeAnnotations: includeAnnotations,
          extractedDocumentId: doc.extracted_document_id,
        });
        if (includeAnnotations) {
          if (response1.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem downloading the document.',
            });
          } else {
            this.$store.dispatch('application/setSuccessNotification', {
              message:
                'You will get a notification when the download has been finished.',
            });
          }
        } else {
          this.downloadDocumentByLink(
            response1.data.document.data.link,
            doc.name,
          );
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },
    async downloadFolder(folder) {
      try {
        this.targetFolder = folder;
        this.downloadFolderDialogOpened = true;
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the folder.',
        });
      }
    },
    async deleteMultipleDocuments() {
      try {
        this.loading = true;
        this.deleteMultipleConfirmationDialogOpened = false;

        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (
            doc.name !== '_soraban' &&
            (!this.disableFolderAction || this.isAdvisor || doc.type === 'file')
          ) {
            const response1 = await API.integration.deleteDocument({
              documentName: doc.name,
              path: doc.path ? doc.path : this.currentPath,
              companyId: this.currentCompanyId,
              isFolder: doc.type === 'folder',
            });
          }
        });
        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've deleted the documents",
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem deleting documents.',
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteDocument(document) {
      try {
        this.loading = true;
        this.deleteConfirmationDialogOpened = false;
        const response = await API.integration.deleteDocument({
          documentName: document.name,
          path: document.path ? document.path : this.currentPath,
          companyId: this.currentCompanyId,
          isFolder: document.type === 'folder',
        });
        this.previewDialogOpened = false;
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async getDocuments(path) {
      if (!isEmpty(this.searchingTerm)) {
        await this.searchDocuments();
        return;
      }

      try {
        const response = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path,
        });

        if (response.data.documents) {
          this.documents = response.data.documents.map(d => ({
            ...d,
            renameFile: false,
            editingDescription: false,
            newDescription: '',
          }));
          this.selectedDocuments = [];
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: "The path doesn't exist",
          });
          this.currentPath = '';
          const response2 = await API.integration.getDocuments({
            companyId: this.currentCompanyId,
          });

          this.documents = response2.data.documents.map(d => ({
            ...d,
            renameFile: false,
            editingDescription: false,
            newDescription: '',
          }));
          if (this.$route.query.path) {
            this.$router.push('/admin-documents');
          }
        }
      } catch (e) {
        console.log(e);
      }
    },

    async searchDocuments() {
      if (isEmpty(this.searchTerm)) {
        this.searchingTerm = '';
        this.goToPath('');
        return;
      }

      try {
        this.loading = true;

        const response = await API.integration.searchDocuments({
          companyId: this.currentCompanyId,
          searchTerm: this.searchTerm,
        });

        this.searchingTerm = this.searchTerm;
        this.documents = response.data.documents.map(d => ({
          ...d,
          renameFile: false,
          editingDescription: false,
          newDescription: '',
        }));
        this.selectedDocuments = [];
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    async createFolder() {
      try {
        this.loading = true;
        this.createFolderDialogOpened = false;
        const response = await API.integration.createFolder({
          path: this.currentPath.concat('/', this.newFolderName),
          companyId: this.currentCompanyId,
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.newFolderName = '';
        this.loading = false;
      }
    },

    startEditingDescription(item) {
      item.editingDescription = true;
      item.newDescription = item.description;
    },

    async saveNewDescription(item) {
      try {
        item.description = item.newDescription;
        item.editingDescription = false;

        const response = await API.extractedDocument.updateDescription({
          companyId: this.currentCompanyId,
          extractedDocumentId: item.extracted_document_id,
          description: item.description,
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Saved description successfully.',
        });
      } catch (error) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'There was a problem saving description. Please try again after refreshing the page.',
        });
      }
    },

    cancelEditingDescription(item) {
      item.editingDescription = false;
    },
  },
  beforeDestroy() {
    document.title = this.previousTitle;
  },
  beforeMount() {
    if (
      !this.currentUser.is_advisor &&
      this.$route.name === 'admin-documents'
    ) {
      this.$router.push({
        path: `/documents${
          this.$route.params.hash ? '/' + this.$route.params.hash : ''
        }`,
        query: this.$route.query,
      });
    }
  },
  async created() {
    if (this.isFirmDocumentsDisabled) {
      this.$store.dispatch('application/setErrorNotification', {
        message: 'You do not have access to this page.',
      });
      this.$router.go(-1);
    }

    const { hash } = this.$route.params;
    const hashId =
      hash && hash !== 'undefined' && hash.substring(hash.lastIndexOf('-') + 1);

    if (this.$route.query.companyId) {
      this.currentCompanyId = parseInt(this.$route.query.companyId);
    } else if (hashId) {
      try {
        const { data } = await API.company.getCompanyIdByHashId(hashId);
        this.currentCompanyId = data.id;
      } catch (error) {
        this.$router.push('/select-entity');
      }
    } else {
      this.currentCompanyId = this.defaultCompany.id;
    }

    this.getClientQuestionnaires();
    this.loading = true;

    try {
      const response = await API.company.getCompany(this.currentCompanyId);
      this.currentCompany = response.data;
      this.previousTitle = document.title;
      document.title = this.currentCompany.name;

      await this.$store.dispatch('resetTiles', {
        hashId: this.currentCompany.hash_id,
        clientName: this.currentCompany.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', ''),
      });

      this.currentPath =
        this.$route.query.path === 'all' ||
        !this.$route.query.path ||
        this.$route.query.path === '/'
          ? ''
          : this.$route.query.path;

      await this.getDocuments(this.currentPath);

      if (this.currentPath !== '') {
        this.currentFolder = this.currentPath.substring(
          this.currentPath.lastIndexOf('/') + 1,
          this.currentPath.length,
        );
      } else {
        this.currentFolder = 'All Documents';
      }

      if (this.$route.query.previewName) {
        const previewDoc = this.documents.find(doc => {
          return doc.name === this.$route.query.previewName;
        });

        if (previewDoc) {
          await this.previewDocument(previewDoc);
        }
      }
    } finally {
      this.loading = false;
    }
  },
  watch: {
    $route: {
      async handler(route) {
        if (this.$route.query.path) {
          try {
            this.loading = true;
            this.currentPath =
              route.query.path === 'all' ? '' : this.$route.query.path;

            this.$router.push(
              `/admin-documents?companyId=${this.currentCompanyId}`,
            );

            const response1 = await API.integration.getDocuments({
              companyId: this.currentCompanyId,
              path: this.currentPath,
            });

            if (response1.data.documents) {
              this.documents = response1.data.documents;
              if (this.currentPath !== '') {
                this.currentFolder = this.currentPath.substring(
                  this.currentPath.lastIndexOf('/') + 1,
                  this.currentPath.length,
                );
              } else {
                this.currentFolder = 'All Documents';
              }
            } else {
              const response2 = await API.integration.getDocuments({
                companyId: this.currentCompanyId,
              });
              this.documents = response2.data.documents;
              this.currentFolder = 'All Documents';
            }
          } finally {
            this.loading = false;
          }
        } else if (
          this.$route.query.currentPath &&
          this.$route.query.currentPath !== this.currentPath
        ) {
          this.goToPath(
            this.$route.query.currentPath === '/'
              ? ''
              : this.$route.query.currentPath,
          );
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.documents {
  &__icon {
    font-size: 18px;
  }
}
.document-action {
  &__icon {
    font-size: 20px;
  }
}
.documents-row {
  &__updated-date {
    font-weight: 200;
    color: rgba(9, 30, 66, 0.6);
  }
}
.image-holder {
  display: block;
  width: 200px;
  height: 200px;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}
.breadcrumbs {
  & >>> .clickable {
    cursor: pointer;
    color: #777;
  }
  & >>> .clickable:hover {
    color: #000;
  }
}

.documents-folder--empty {
  color: var(--gray-500);
}

.documents-folder--empty > span:first-child:after {
  padding-left: 6px;

  color: var(--gray-200);

  content: ' (Empty)';
  font-style: italic;
}
</style>
